import type { AudioSource, AudioSourceHandlers } from '@soniox/client'
import { getServiceStrings } from '@/services/i18n'
import {
  borrowActiveTabAudioCapture,
  setActiveTabAudioCapture,
  takePreparedTabAudioCapture,
  type PreparedTabAudioCapture,
} from '@/audio/preparedTabAudioCapture'
import { createPcmAudioTap, type PcmAudioTap } from '@/audio/createPcmAudioTap'
import { createMicGate } from '@/audio/createMicGate'
import { CaptureLostError } from '@/audio/CaptureLostError'
import { SCREEN_SHARE_VIDEO_CONSTRAINTS } from '@/audio/displayCaptureConstraints'

// Two legs sum into this mix: the shared tab and the local microphone, both at
// unity gain. Independent sources, so the sum reaches up to 2.0 — and the PCM
// encoder feeding STT hard-clamps at 1.0, as does the archive encoder. Any moment
// of double-talk was therefore hard-clipped, which is audible as crackle and is
// exactly the kind of distortion a recogniser has no defence against (BRAI-362).
//
// A limiter on the sum fixes that without making the common case quieter, which a
// blanket -6 dB of headroom would. Threshold sits low enough that ordinary
// single-talk never reaches it, so it only acts when the two legs genuinely
// overlap. Chrome's compressor look-aheads internally, so it does not let a
// transient through the way a zero-latency feed-forward limiter would.
const MIX_LIMITER_THRESHOLD_DB = -6
const MIX_LIMITER_KNEE_DB = 0
const MIX_LIMITER_RATIO = 20
const MIX_LIMITER_ATTACK_SECONDS = 0.003
const MIX_LIMITER_RELEASE_SECONDS = 0.25

interface DisplayMediaSourceOptions {
  microphoneDeviceId?: string
  /** False when the meeting was set up without a microphone — tab audio only, no mixing/gate. */
  hasMicrophone?: boolean
  onMixedStream?: (stream: MediaStream) => void
}

interface DisplayMediaSourceStopOptions {
  /** Keep tab/mic streams alive for a later restart in the same meeting. */
  keepStreams?: boolean
}

function getMicrophoneConstraints(deviceId?: string): MediaTrackConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  }
}

/**
 * AudioSource implementation that captures audio from a browser tab or system audio
 * via getDisplayMedia. Designed as a drop-in replacement for Soniox's MicrophoneSource
 * when the user wants to transcribe audio playing in another tab (e.g. Zoom, Meet).
 *
 * This bypasses the Bluetooth HFP mode problem: instead of routing tab audio through
 * the microphone (which fails in HFP mode), it captures the raw tab audio stream directly.
 */
export class DisplayMediaSource implements AudioSource {
  private tap: PcmAudioTap | null = null
  private handlers: AudioSourceHandlers | null = null
  private stream: MediaStream | null = null
  private displayStream: MediaStream | null = null
  private microphoneStream: MediaStream | null = null
  /** The user's mute switch, remembered so `resume` cannot quietly undo it. */
  private micEnabled = true
  private audioContext: AudioContext | null = null
  private readonly microphoneDeviceId: string
  private readonly hasMicrophone: boolean
  private readonly onMixedStream?: (stream: MediaStream) => void

  constructor(options: DisplayMediaSourceOptions = {}) {
    this.microphoneDeviceId = options.microphoneDeviceId ?? ''
    this.hasMicrophone = options.hasMicrophone ?? true
    this.onMixedStream = options.onMixedStream
  }

  async start(handlers: AudioSourceHandlers): Promise<void> {
    this.handlers = handlers
    const prepared = takePreparedTabAudioCapture()
    const active = prepared ? null : borrowActiveTabAudioCapture()

    if (prepared) {
      this.applyCapture(prepared)
      setActiveTabAudioCapture(prepared)
    } else if (active) {
      this.applyCapture(active)
    } else {
      // Request screen/tab sharing. A video track is required by most browsers
      // to show the share dialog. Keep video tracks (disabled) for screen-capture reuse.
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: SCREEN_SHARE_VIDEO_CONSTRAINTS,
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      })
      this.displayStream = displayStream

      displayStream.getVideoTracks().forEach((track) => {
        track.enabled = false
      })

      const audioTracks = displayStream.getAudioTracks()
      if (audioTracks.length === 0) {
        displayStream.getTracks().forEach((t) => t.stop())
        throw new Error(getServiceStrings().audioTrackMissing)
      }

      if (this.hasMicrophone) {
        const microphoneStream = await navigator.mediaDevices.getUserMedia({
          audio: getMicrophoneConstraints(this.microphoneDeviceId),
          video: false,
        })
        this.microphoneStream = microphoneStream

        const microphoneTracks = microphoneStream.getAudioTracks()
        if (microphoneTracks.length === 0) {
          this.stop()
          throw new Error(getServiceStrings().microphoneTrackMissing)
        }
      }

      setActiveTabAudioCapture({
        displayStream: this.displayStream,
        microphoneStream: this.microphoneStream,
      })
    }

    const audioTracks = this.displayStream!.getAudioTracks()
    const microphoneTracks = this.microphoneStream?.getAudioTracks() ?? []

    this.audioContext = new AudioContext()
    const destination = this.audioContext.createMediaStreamDestination()
    const displaySource = this.audioContext.createMediaStreamSource(new MediaStream(audioTracks))

    // Both legs meet at the limiter rather than at the destination — but only when
    // there are two of them. Tab-only has nothing to sum and so nothing to limit,
    // and keeps the graph it always had.
    const limiter = microphoneTracks.length > 0 ? this.createMixLimiter(this.audioContext) : null
    const mixBus = limiter ?? destination
    limiter?.connect(destination)

    displaySource.connect(mixBus)

    if (microphoneTracks.length > 0) {
      const microphoneSource = this.audioContext.createMediaStreamSource(
        new MediaStream(microphoneTracks)
      )
      microphoneSource.connect(await createMicGate(this.audioContext)).connect(mixBus)
    }

    this.stream = destination.stream
    this.onMixedStream?.(destination.stream.clone())

    // Notify when the user stops sharing (e.g. clicks "Stop sharing" banner)
    audioTracks.forEach((track) => {
      track.onended = () => {
        handlers.onError(new CaptureLostError(getServiceStrings().screenShareStopped))
      }
    })

    // Note: this AudioContext is intentionally left at its default/native
    // sample rate (not forced to 16kHz) because `destination.stream` is also
    // forwarded to the local meeting archive recorder via `onMixedStream`;
    // the PCM worklet tap below resamples to 16kHz independently for STT.
    this.tap = await createPcmAudioTap(this.audioContext, this.stream, {
      onData: handlers.onData,
      onError: () => handlers.onError(new Error(getServiceStrings().mediaRecorderErrorTab)),
    })
  }

  /** Soft limiter for the summed mix — see the MIX_LIMITER_* constants. */
  private createMixLimiter(audioContext: AudioContext): DynamicsCompressorNode {
    const limiter = audioContext.createDynamicsCompressor()
    limiter.threshold.value = MIX_LIMITER_THRESHOLD_DB
    limiter.knee.value = MIX_LIMITER_KNEE_DB
    limiter.ratio.value = MIX_LIMITER_RATIO
    limiter.attack.value = MIX_LIMITER_ATTACK_SECONDS
    limiter.release.value = MIX_LIMITER_RELEASE_SECONDS
    return limiter
  }

  private applyCapture(capture: PreparedTabAudioCapture): void {
    this.displayStream = capture.displayStream
    this.microphoneStream = capture.microphoneStream
  }

  /** Mirrors the user's mute switch onto the microphone leg of the mix — now,
   *  and after a resume. The shared tab is a separate leg and is unaffected. */
  setMicrophoneEnabled(enabled: boolean): void {
    this.micEnabled = enabled
    this.setMicrophoneTracksEnabled(enabled)
  }

  private setMicrophoneTracksEnabled(enabled: boolean): void {
    this.microphoneStream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled
    })
  }

  stop(options: DisplayMediaSourceStopOptions = {}): void {
    this.tap?.dispose()
    this.tap = null

    if (options.keepStreams && this.displayStream) {
      setActiveTabAudioCapture({
        displayStream: this.displayStream,
        microphoneStream: this.microphoneStream,
      })
      void this.audioContext?.close().catch(() => {
        /* ignore close errors */
      })
      this.stream = null
      this.audioContext = null
      this.handlers = null
      return
    }

    this.stream?.getTracks().forEach((t) => t.stop())
    this.displayStream?.getTracks().forEach((t) => t.stop())
    this.microphoneStream?.getTracks().forEach((t) => t.stop())
    void this.audioContext?.close().catch(() => {
      /* ignore close errors */
    })
    this.stream = null
    this.displayStream = null
    this.microphoneStream = null
    this.audioContext = null
    this.handlers = null
  }

  /**
   * Mutes both tab and microphone audio tracks while leaving the PCM tap
   * running. Web Audio keeps delivering silent frames from disabled tracks,
   * so the STT proxy socket keeps receiving data and Soniox does not time
   * out the session during a user-initiated pause.
   */
  pause(): void {
    this.displayStream?.getAudioTracks().forEach((track) => {
      track.enabled = false
    })
    this.setMicrophoneTracksEnabled(false)
  }

  /**
   * Brings the tab back unconditionally, and the microphone back only as far
   * as the user's mute switch allows. Resuming used to enable both legs
   * outright, handing a muted meeting a live microphone again (BRAI-403).
   */
  resume(): void {
    this.displayStream?.getAudioTracks().forEach((track) => {
      track.enabled = true
    })
    this.setMicrophoneTracksEnabled(this.micEnabled)
  }

  /** Recreates the PCM tap on the same mixed stream/context without releasing the capture devices. */
  restart(nextHandlers?: AudioSourceHandlers): void {
    // Handlers are replaceable because a reconnect after a network outage taps
    // the SAME capture for a BRAND NEW socket: the stored handlers close over
    // the dead one, so reusing them would pipe PCM into a closed WebSocket.
    if (nextHandlers) this.handlers = nextHandlers
    if (!this.stream || !this.audioContext || !this.handlers) return
    const { stream, audioContext, handlers } = this
    this.tap?.dispose()
    this.tap = null
    void createPcmAudioTap(audioContext, stream, {
      onData: handlers.onData,
      onError: () => handlers.onError(new Error(getServiceStrings().mediaRecorderErrorTab)),
    })
      .then((tap) => {
        this.tap = tap
      })
      .catch(() => {
        /* ignore restart errors */
      })
  }
}
